import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MapIcon from '@mui/icons-material/Map';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { ModernCard } from './ModernComponents';
import './styles/recapitulatif.style.scss';

const MAX_ITEMS_DISPLAY = 5;
const MAX_PARCEL_CONSTRAINTS_DISPLAY = 2;

const CONSTRAINT_TYPE_CLASS = {
  obligation: 'recapConstraintTypeObligation',
  interdiction: 'recapConstraintTypeInterdiction',
  division: 'recapConstraintTypeDivision',
  permission: 'recapConstraintTypePermission',
  conversion: 'recapConstraintTypeConversionBio',
};

/**
 * @param {Object} props
 * @param {Array} props.parcelsList
 * @param {Array} props.culturesList
 * @param {Array} props.constraintsList
 * @param {Array} props.parcelConstraintsList
 * @param {Array} props.strategiesList
 * @param {number} props.yearsToSimulate
 * @param {string} props.title
 */
const Recapitulatif = ({
  parcelsList = [],
  culturesList = [],
  constraintsList = [],
  parcelConstraintsList = [],
  strategiesList = [],
  yearsToSimulate = 3,
  title,
}) => {
  const { t } = useTranslation();
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCardExpansion = (cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  return (
    <div className="recapitulatif">
      <ModernCard title={title} icon={AssignmentTurnedInIcon}>
        <div className="recapGrid">
          <div
            className={`recapItem ${parcelsList.length > MAX_ITEMS_DISPLAY ? 'recapItemExpandable' : ''} ${expandedCards['parcels'] ? 'recapItemExpanded' : ''}`}
            onClick={() => parcelsList.length > MAX_ITEMS_DISPLAY && toggleCardExpansion('parcels')}
          >
            <h3 className="recapItemTitle">
              <MapIcon sx={{ fontSize: 16 }} />
              {t('simulation.step.summary.parcelsTitle', { count: parcelsList.length })}
            </h3>
            <ul className="recapList">
              {parcelsList
                .slice(0, expandedCards['parcels'] ? parcelsList.length : MAX_ITEMS_DISPLAY)
                .map((name, index) => (
                  <li key={index}>{name}</li>
                ))}
            </ul>
            {parcelsList.length > MAX_ITEMS_DISPLAY && !expandedCards['parcels'] && (
              <div className="recapMore">
                {t('simulation.step.summary.more', {
                  count: parcelsList.length - MAX_ITEMS_DISPLAY,
                })}
              </div>
            )}
          </div>

          <div
            className={`recapItem ${culturesList.length > MAX_ITEMS_DISPLAY ? 'recapItemExpandable' : ''} ${expandedCards['cultures'] ? 'recapItemExpanded' : ''}`}
            onClick={() =>
              culturesList.length > MAX_ITEMS_DISPLAY && toggleCardExpansion('cultures')
            }
          >
            <h3 className="recapItemTitle">
              <AgricultureIcon sx={{ fontSize: 16 }} />
              {t('simulation.step.summary.culturesTitle', { count: culturesList.length })}
            </h3>
            <ul className="recapList recapListCultures">
              {culturesList
                .slice(0, expandedCards['cultures'] ? culturesList.length : MAX_ITEMS_DISPLAY)
                .map((culture, index) => (
                  <li key={index} className="recapCultureItem">
                    <span className="recapCultureName">{culture?.name}</span>
                    <span className="recapCultureConstraints">
                      {culture?.min > 0 && (
                        <span className="recapConstraintBadge">
                          {t('simulation.step.summary.min')} {culture?.min} {culture?.unit}
                        </span>
                      )}
                      {culture?.max > 0 && (
                        <span className="recapConstraintBadge">
                          {t('simulation.step.summary.max')} {culture?.max} {culture?.unit}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
            </ul>
            {culturesList.length > MAX_ITEMS_DISPLAY && !expandedCards['cultures'] && (
              <div className="recapMore">
                {t('simulation.step.summary.more', {
                  count: culturesList.length - MAX_ITEMS_DISPLAY,
                })}
              </div>
            )}
          </div>

          <div
            className={`recapItem ${constraintsList.length > MAX_ITEMS_DISPLAY ? 'recapItemExpandable' : ''} ${expandedCards['constraints'] ? 'recapItemExpanded' : ''}`}
            onClick={() =>
              constraintsList.length > MAX_ITEMS_DISPLAY && toggleCardExpansion('constraints')
            }
          >
            <h3 className="recapItemTitle">
              <SettingsIcon sx={{ fontSize: 16 }} />
              {t('simulation.step.summary.constraintsTitle', { count: constraintsList.length })}
            </h3>
            <ul className="recapList">
              {constraintsList
                .slice(0, expandedCards['constraints'] ? constraintsList.length : MAX_ITEMS_DISPLAY)
                .map((name, index) => (
                  <li key={index}>{name}</li>
                ))}
            </ul>
            {constraintsList.length > MAX_ITEMS_DISPLAY && !expandedCards['constraints'] && (
              <div className="recapMore">
                {t('simulation.step.summary.more', {
                  count: constraintsList.length - MAX_ITEMS_DISPLAY,
                })}
              </div>
            )}
          </div>

          <div
            className={`recapItem ${parcelConstraintsList.length > MAX_PARCEL_CONSTRAINTS_DISPLAY ? 'recapItemExpandable' : ''} ${expandedCards['parcelConstraints'] ? 'recapItemExpanded' : ''}`}
            onClick={() =>
              parcelConstraintsList.length > MAX_PARCEL_CONSTRAINTS_DISPLAY &&
              toggleCardExpansion('parcelConstraints')
            }
          >
            <h3 className="recapItemTitle">
              <TuneIcon sx={{ fontSize: 16 }} />
              {t('simulation.navigation.parcelConstraints')} ({parcelConstraintsList.length})
            </h3>
            <ul className="recapList recapListParcelConstraints">
              {parcelConstraintsList
                .slice(
                  0,
                  expandedCards['parcelConstraints']
                    ? parcelConstraintsList.length
                    : MAX_PARCEL_CONSTRAINTS_DISPLAY,
                )
                .map((item, index) => (
                  <li key={index} className="recapParcelConstraintItem">
                    <div
                      className={`recapConstraintType ${CONSTRAINT_TYPE_CLASS[item.type] ?? ''}`}
                    >
                      {item.typeLabel}
                    </div>
                    <div className="recapConstraintDetails">
                      {item.type !== 'conversion' && (
                        <div className="recapConstraintRow">
                          <span className="recapConstraintLabel">
                            {t('common.cultures')}
                          </span>
                          <span className="recapConstraintValue">{item.cultureNames}</span>
                        </div>
                      )}
                      <div className="recapConstraintRow">
                        <span className="recapConstraintLabel">
                          {t('common.parcels')}
                        </span>
                        <span className="recapConstraintValue">{item.parcelNames}</span>
                      </div>
                      <div className="recapConstraintRow">
                        <span className="recapConstraintLabel">
                          {t('common.campaigns')}
                        </span>
                        <span className="recapConstraintValue">{item.campagnesLabel}</span>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
            {parcelConstraintsList.length > MAX_PARCEL_CONSTRAINTS_DISPLAY &&
              !expandedCards['parcelConstraints'] && (
                <div className="recapMore">
                  {t('simulation.step.summary.more', {
                    count: parcelConstraintsList.length - MAX_PARCEL_CONSTRAINTS_DISPLAY,
                  })}
                </div>
              )}
          </div>

          <div className="recapItem">
            <h3 className="recapItemTitle">
              <TrackChangesIcon sx={{ fontSize: 16 }} />
              {t('simulation.step.summary.strategiesTitle')}
            </h3>
            <ul className="recapList">
              {strategiesList.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
              <li className="recapDivider">
                {' '}
                {t('simulation.step.summary.yearsToSimulate', { count: yearsToSimulate })}
              </li>
            </ul>
          </div>
        </div>
      </ModernCard>
    </div>
  );
};

export default Recapitulatif;
